import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import MainLayout from '@/components/layout/MainLayout';
import { SEOHead } from '@/components/seo/SEOHead';
import { NotFoundState } from '@/components/common/NotFoundState';
import { useLang } from '@/i18n/useLocalizedPath';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft,
  Calendar,
  User,
  MapPin,
  Tag,
  Loader2,
  FileText,
  Share2,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { trackContentView } from '@/utils/analytics';
import { BoostBanner } from '@/components/boost/QuickShareButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function BlogPostPage() {
  const { slug } = useParams();
  const lang = useLang();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetchBlog();
  }, [slug, lang]);

  const fetchBlog = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/public/blogs/${slug}?lang=${lang}`);
      setBlog(response.data);
      
      // Track content view
      if (response.data) {
        trackContentView('blog_post', response.data.id, response.data.title);
      }
    } catch (err) {
      console.error('Failed to load blog:', err);
      if (err.response?.status === 404) {
        setNotFound(true);
      } else {
        setError('Failed to load article');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: blog.title,
          text: blog.excerpt,
          url: url
        });
      } catch (err) {
        // User cancelled or error
      }
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    }
  };

  const estimateReadTime = (content) => {
    const wordsPerMinute = 200;
    const words = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
    return Math.ceil(words / wordsPerMinute);
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-screen flex justify-center items-center">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
        </div>
      </MainLayout>
    );
  }

  if (notFound) {
    return (
      <MainLayout>
        <NotFoundState
          title="Article not found"
          primaryTo="/blog"
          primaryLabel="Browse blog"
        />
      </MainLayout>
    );
  }

  if (error || !blog) {
    return (
      <MainLayout>
        <div className="min-h-screen flex flex-col justify-center items-center px-4 text-center">
          <FileText className="w-16 h-16 text-gray-300 mb-4" />
          <h1 className="text-2xl font-bold text-gray-700 mb-2">We couldn't load this article</h1>
          <p className="text-gray-500 mb-4">Please try again in a moment.</p>
          <Link to="/blog">
            <Button className="bg-orange-600 hover:bg-orange-700" data-testid="blog-post-fallback-button">
              Browse All Articles
            </Button>
          </Link>
        </div>
      </MainLayout>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": blog.title,
    "description": blog.excerpt,
    "author": {
      "@type": "Organization",
      "name": blog.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "Jigsaw Conferences",
      "url": "https://events.jigsawconferences.co.uk"
    },
    "datePublished": blog.publishDate,
    "dateModified": blog.updatedAt,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://events.jigsawconferences.co.uk/blog/${blog.slug}`
    },
    "image": blog.featuredImage || undefined
  };

  return (
    <MainLayout>
      <SEOHead
        title={blog.metaTitle || blog.title}
        description={blog.metaDescription || blog.excerpt}
        url={`https://events.jigsawconferences.co.uk/blog/${blog.slug}`}
        image={blog.featuredImage}
        schema={articleSchema}
      />

      <article className="min-h-screen bg-white">
        {/* Header */}
        <header className="bg-gray-50 py-12 border-b">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to="/blog" className="text-sm text-gray-500 hover:text-gray-700 flex items-center mb-4">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Blog
            </Link>
            
            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-4">
              {blog.sectorTags?.map((tag, i) => (
                <Link key={i} to={`/blog?sector=${encodeURIComponent(tag)}`}>
                  <Badge variant="secondary" className="hover:bg-gray-200">
                    {tag}
                  </Badge>
                </Link>
              ))}
              {blog.city && (
                <Link to={`/blog?city=${blog.city}`}>
                  <Badge variant="outline" className="hover:bg-gray-100">
                    <MapPin className="w-3 h-3 mr-1" />
                    {blog.city}
                  </Badge>
                </Link>
              )}
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {blog.title}
            </h1>
            
            <p className="text-xl text-gray-600 mb-6">
              {blog.excerpt}
            </p>
            
            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center">
                <User className="w-4 h-4 mr-1" />
                {blog.author}
              </span>
              <span className="flex items-center">
                <Calendar className="w-4 h-4 mr-1" />
                {new Date(blog.publishDate).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </span>
              <span className="flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                {estimateReadTime(blog.content)} min read
              </span>
              <button
                onClick={handleShare}
                className="flex items-center hover:text-orange-600 transition-colors"
              >
                <Share2 className="w-4 h-4 mr-1" />
                Share
              </button>
            </div>
          </div>
        </header>

        {/* Featured Image */}
        {blog.featuredImage && (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mb-8 relative z-10">
            <div className="rounded-lg overflow-hidden shadow-lg -mt-6">
              <img
                src={blog.featuredImage}
                alt={blog.title}
                className="w-full h-auto"
              />
            </div>
          </div>
        )}

        {/* Content */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div 
            className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-a:text-orange-600 prose-a:no-underline hover:prose-a:underline"
            dangerouslySetInnerHTML={{ __html: blog.content }}
          />
        </div>

        {/* Related Event CTA */}
        {blog.featuredEventId && (
          <section className="bg-orange-50 py-12 border-t">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Interested in This Event?
              </h2>
              <p className="text-gray-600 mb-6">
                View full event details and find group accommodation options.
              </p>
              <Button className="bg-orange-600 hover:bg-orange-700">
                View Event Details
              </Button>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="py-12 border-t">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="bg-gray-900 text-white">
              <CardContent className="p-8 text-center">
                <h2 className="text-2xl font-bold mb-4">
                  Need Group Accommodation?
                </h2>
                <p className="text-gray-300 mb-6">
                  Jigsaw Conferences offers free venue finding and group accommodation services for business events.
                </p>
                <Link to="/events/list-your-event">
                  <Button className="bg-orange-600 hover:bg-orange-700">
                    Get a Free Quote
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Event Boost CTA */}
        <section className="py-8">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <BoostBanner variant="compact" />
          </div>
        </section>

        {/* More Articles */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-xl font-bold text-gray-900 mb-6">More Articles</h2>
            <Link to="/blog">
              <Button variant="outline">
                Browse All Articles
              </Button>
            </Link>
          </div>
        </section>
      </article>
    </MainLayout>
  );
}
